import React, { useCallback, useEffect, useState } from 'react';

import { chevronBackOutline, close } from 'ionicons/icons';

import Comprobante from '../../../components/Reintegro/Comprobante';
import Documentacion from '../../../components/Reintegro/Documentacion';
import VeterinarioForm from '../../../components/Reintegro/VeterinarioForm';
import { useComprobanteManager } from '../../../hooks/Reintegro/Comprobante/useComprobanteManager';
import { useAlert } from '../../../hooks/UI/useAlert';
import { useStepNavigation } from '../../../hooks/UI/useStepNavigation';
import Alert from '../../UI/Alert';
import Navigation from '../../UI/Navigation';
import './WizardContent.scss';
import { WizardContentProps } from './types';

const WizardContent: React.FC<WizardContentProps> = ({
  documentacionCompleta,
  isVacuna,
  onClose,
  submittedVetValues,
  mutateVacunaComprobantes,
}) => {
  // Calcular pasos: vacuna no tiene veterinario
  const getSteps = () => {
    if (isVacuna) {
      return documentacionCompleta ? 1 : 2; // Solo Comprobante o Documentacion + Comprobante
    }
    return documentacionCompleta ? 2 : 3; // Veterinario + [Documentacion] + Comprobante
  };

  const steps = getSteps();
  const { currentStep, totalSteps, isFirst, next, back } = useStepNavigation(steps);
  const { alertState, showAlert, hideAlert } = useAlert();
  const comprobanteManager = useComprobanteManager(isVacuna);
  const [isCompleted, setIsCompleted] = useState(false);

  const showProgress = !(isVacuna && documentacionCompleta);

  useEffect(() => {
    console.log('[WizardContent] step state', {
      isVacuna,
      documentacionCompleta,
      currentStep,
      totalSteps,
      submittedVetValues: submittedVetValues.current,
    });
  }, [currentStep, documentacionCompleta, isVacuna, submittedVetValues, totalSteps]);

  const handleBack = useCallback(() => {
    if (isFirst) {
      onClose();
    } else {
      back();
    }
  }, [isFirst, onClose, back]);

  const handleVetNext = useCallback(() => {
    console.log('[WizardContent] veterinarian step completed', {
      currentStep,
      totalSteps,
      submittedVetValues: submittedVetValues.current,
    });

    next();
  }, [currentStep, next, submittedVetValues, totalSteps]);

  return (
    <>
      <Navigation
        startIcon={isFirst ? close : chevronBackOutline}
        onBack={handleBack}
        currentStep={showProgress ? currentStep : undefined}
        totalSteps={showProgress ? totalSteps : undefined}
        isCompleted={isCompleted}
      />

      {/* Renderizar solo el paso actual */}
      {(() => {
        // Determinar qué paso mostrar según currentStep
        const docStep = isVacuna ? 1 : 2;
        const comprobanteStep = totalSteps;

        // Paso Veterinario (solo si NO es vacuna y es step 1)
        if (!isVacuna && currentStep === 1) {
          return (
            <div className="step">
              <VeterinarioForm
                submittedVetValues={submittedVetValues}
                onNext={handleVetNext}
                showAlert={showAlert}
              />
            </div>
          );
        }

        // Paso Documentación (solo si falta y es el step correspondiente)
        if (!documentacionCompleta && currentStep === docStep) {
          return (
            <div className="step">
              <Documentacion onNext={next} />
            </div>
          );
        }

        // Último paso: Comprobante
        if (currentStep === comprobanteStep) {
          return (
            <div className="step">
              <Comprobante onSuccess={onClose} onClose={onClose} onComplete={() => setIsCompleted(true)} manager={comprobanteManager} isVacuna={isVacuna} mutateVacunaComprobantes={mutateVacunaComprobantes} />
            </div>
          );
        }

        return null;
      })()}

      <Alert
        isOpen={alertState.isOpen}
        header={alertState.header}
        message={alertState.message}
        buttons={alertState.buttons}
        onDidDismiss={hideAlert}
      />
    </>
  );
};

export default React.memo(WizardContent);
